import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function answerPreview(answers = []) {
  return (answers || [])
    .map((a) => stripHtml(a.answer_text || a.text || ""))
    .filter(Boolean)
    .join(" ");
}

/**
 * Multi-select picker to pin existing bank questions onto a quiz.
 * Only returns refs + display DTOs — never deletes or mutates bank rows.
 */
export default function QuizAddQuestionsDialog({
  open,
  onClose,
  onAdd,
  privateQuestions = [],
  microlabQuestions = [],
  microlabCatalogue = "histology",
  onMicrolabCatalogueChange,
  loadingPrivate = false,
  loadingMicrolab = false,
  alreadyPinnedKeys = [],
}) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === "dark";
  const [tab, setTab] = useState("private");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const pinned = useMemo(() => new Set(alreadyPinnedKeys.map(String)), [alreadyPinnedKeys]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelected(new Set());
    setTab("private");
  }, [open]);

  const source = tab === "microlab" ? microlabQuestions : privateQuestions;
  const loading = tab === "microlab" ? loadingMicrolab : loadingPrivate;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (source || []).filter((item) => {
      const key = `${tab}:${item.id}`;
      if (pinned.has(key)) return false;
      if (!q) return true;
      const body = stripHtml(item.question_content || item.content || item.title).toLowerCase();
      const answers = answerPreview(item.answers).toLowerCase();
      const title = String(item.quiz_title || item.title || "").toLowerCase();
      return body.includes(q) || answers.includes(q) || title.includes(q);
    });
  }, [source, search, tab, pinned]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = `${tab}:${id}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAdd = () => {
    const picks = [];
    selected.forEach((key) => {
      const [bank, id] = key.split(":");
      const list = bank === "microlab" ? microlabQuestions : privateQuestions;
      const row = (list || []).find((item) => String(item.id) === String(id));
      if (row) picks.push({ bank, row });
    });
    if (!picks.length) return;
    onAdd?.(picks);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.card",
          color: theme.palette.text.main,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1.5,
          pb: 0.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            Add questions to quiz
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pins copies of bank refs onto this quiz only — originals stay unchanged.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value);
            setSearch("");
          }}
          sx={{ mb: 1.5, minHeight: 40 }}
        >
          <Tab value="private" label="Private bank" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab value="microlab" label="Microlab bank" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1.5}>
          <TextField
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          {tab === "microlab" && onMicrolabCatalogueChange ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Catalogue</InputLabel>
              <Select
                label="Catalogue"
                value={microlabCatalogue}
                onChange={(e) => onMicrolabCatalogueChange(e.target.value)}
              >
                <MenuItem value="histology">Histology</MenuItem>
                <MenuItem value="embryology">Embryology</MenuItem>
                <MenuItem value="anatomy">Anatomy</MenuItem>
              </Select>
            </FormControl>
          ) : null}
        </Stack>

        {loading ? (
          <Box py={5} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!loading && !filtered.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {search.trim()
              ? "No matching questions left to add."
              : "No available questions in this bank (or all are already pinned)."}
          </Typography>
        ) : null}

        <Stack
          spacing={0.75}
          sx={{
            maxHeight: 420,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {filtered.map((item) => {
            const key = `${tab}:${item.id}`;
            const checked = selected.has(key);
            const body = stripHtml(item.question_content || item.content || item.title);
            return (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => toggle(item.id)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  textAlign: "left",
                  border: `1px solid ${alpha(theme.palette.divider, checked ? 0.9 : 0.55)}`,
                  borderRadius: 2,
                  p: 1.1,
                  m: 0,
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                  bgcolor: checked
                    ? alpha(theme.palette.info.main, darkMode ? 0.16 : 0.08)
                    : darkMode
                      ? alpha("#fff", 0.02)
                      : alpha(theme.palette.info.main, 0.02),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.info.main, darkMode ? 0.2 : 0.1),
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{ mt: -0.25, p: 0.5 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" mb={0.35} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={tab === "microlab" ? "Microlab" : "Private"}
                      sx={{ height: 20, fontWeight: 600 }}
                    />
                    {item.difficulty || item.level ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={String(item.difficulty || item.level)}
                        sx={{ height: 20, textTransform: "capitalize" }}
                      />
                    ) : null}
                    {item.quiz_title ? (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.quiz_title}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {body || "Untitled question"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          {selected.size} selected
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="info"
          disabled={!selected.size}
          onClick={handleAdd}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Add to quiz
        </Button>
      </DialogActions>
    </Dialog>
  );
}

QuizAddQuestionsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  privateQuestions: PropTypes.array,
  microlabQuestions: PropTypes.array,
  microlabCatalogue: PropTypes.string,
  onMicrolabCatalogueChange: PropTypes.func,
  loadingPrivate: PropTypes.bool,
  loadingMicrolab: PropTypes.bool,
  alreadyPinnedKeys: PropTypes.arrayOf(PropTypes.string),
};
